import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Card, CardContent, Divider, Grid, makeStyles, Typography, IconButton } from '@material-ui/core';
import { createStyles } from '@material-ui/styles';
import { getJacketFields, getRateTypes } from 'api/jacket-api';
import { PolicyFieldsModel, RateTypes } from 'features/jackets/types';
import useAsync from 'hooks/useAsync';
import { useEffect, useState } from 'react';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import { DeleteOutlineIcon } from 'ui-kit/icons/DeleteOutlineIcon';
import { AgentNetDropdownSelector, CurrencyField } from 'ui-kit/inputs';
import { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import AutocompletePolicySearch, { searchType } from 'ui-kit/inputs/AutocompletePolicySearch/AutocompletePolicySearch';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import { formatDate } from 'utilities/dateFormatter';
import { FieldValidationError } from 'utilities/validation/validation';
import { PolicyInventoryType } from 'features/orderManagement/types';
import { statCodeStateList } from 'features/orderManagement/createNewFileConstants';

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      border: '1px solid #E0E0E0',
      borderRadius: pxToRem(4),
      boxShadow: 'none',
    },
    cardContent: {
      padding: 0,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(0.625),
    },
    headerText: {
      flex: 1,
      padding: theme.spacing(1.25, 0, 0, 1.25),
    },
    title: {
      fontSize: pxToRem(16),
      fontWeight: 500,
      color: '#212121',
      marginBottom: theme.spacing(0.5),
    },
    subtitle: {
      fontSize: pxToRem(14),
    },
    deleteButton: {
      color: '#1976D2',
      '&:hover': {
        color: '#1565C0',
        backgroundColor: 'transparent',
      },
      top: pxToRem(15),
      right: pxToRem(30),
    },
    deleteButtonNoPolicy: {
      color: '#1976D2',
      '&:hover': {
        color: '#1565C0',
        backgroundColor: 'transparent',
      },
      top: pxToRem(4),
      right: pxToRem(30),
    },
    policyFields: {
      marginTop: theme.spacing(1.25),
      padding: theme.spacing(1.25, 0, 0, 1.25),
    },
  }),
);

export interface VirtualJacket {
  id: string;
  policyNumber: string;
  policyDescription: string;
  itemNumber: string;
  liabilityAmount: number;
  policyDate: string;
  reportedGross: number;
  reportedNet: number;
  rateTypeId: string;
  statCode: string;
  priorLiabilityAmount?: number;
  priorPolicyDate?: string;
}

interface VirtualJacketCardProps {
  jacket: VirtualJacket;
  policyInventory: PolicyInventoryType[];
  stateCode: string;
  countyCode: string;
  accountNumber: number;
  selectedPolicies: string[];
  onChange: (jacket: VirtualJacket) => void;
  onDelete: () => void;
  validationErrors?: FieldValidationError[];
}

function findPolicy(
  input: string | undefined,
  stateCode: string,
  policies: PolicyInventoryType[],
): PolicyInventoryType | null {
  if (!input) {
    return null;
  }
  for (const policy of policies) {
    if (!policy.stateList.includes(stateCode)) {
      continue;
    }
    const foundPolicy = policy.policyNumbers.find((policyNumber) => policyNumber === input);
    if (foundPolicy) {
      return policy;
    }
  }
  return null;
}

function getSearchPolicies(
  input: string | undefined,
  stateCode: string,
  policies: PolicyInventoryType[],
  selectedPolicies: string[],
): searchType[] {
  const searchTypes: searchType[] = [];
  const uniqueSet = new Set<string>();
  for (const policy of policies) {
    if (!policy.stateList.includes(stateCode)) {
      continue;
    }
    for (const policyNumber of policy.policyNumbers) {
      if (selectedPolicies.includes(policyNumber) || uniqueSet.has(policyNumber)) {
        continue;
      }
      uniqueSet.add(policyNumber);
      if (!input || policyNumber.toLowerCase().includes(input.toLowerCase())) {
        searchTypes.push({
          name: policyNumber,
          value: policyNumber,
          display: policy.itemDescription,
          isActive: true,
        });
      }
    }
  }
  return searchTypes;
}

function getMaxDateString(): string {
  const today = new Date();
  return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
}

export default function VirtualJacketCard({
  jacket,
  policyInventory,
  stateCode,
  countyCode,
  accountNumber,
  selectedPolicies,
  onChange,
  onDelete,
  validationErrors,
}: VirtualJacketCardProps) {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();

  const [policy, setPolicy] = useState<PolicyInventoryType | null>(null);
  const [searchPolicyNumberInput, setSearchPolicyNumberInput] = useState<string>('');
  const [policyDate, setPolicyDate] = useState<string>(jacket.policyDate);
  const [rateTypeId, setRateTypeId] = useState<string>(jacket.rateTypeId);
  const [rateTypesSelections, setRateTypesSelections] = useState<SelectOption[]>([]);
  const [statCodesSelections, setStatCodesSelections] = useState<SelectOption[]>([]);
  const [statCodeRequired, setStatCodeRequired] = useState<boolean>(false);

  useEffect(() => {
    setStatCodeRequired(statCodeStateList.includes(stateCode));
  }, [stateCode]);

  const getJacketFieldsApi = async (): Promise<PolicyFieldsModel> => {
    const token = await getAccessToken();
    return await getJacketFields(policy?.itemNo ?? '0', accountNumber, stateCode, countyCode, token);
  };

  const { execute: executeGetJacketFields, value: getJacketFieldsValue } = useAsync<PolicyFieldsModel>(
    getJacketFieldsApi,
    false,
  );

  const getRateTypesApi = async (): Promise<RateTypes[]> => {
    if (!policy) {
      return [{ rateTypeId: '1', rateTypeName: 'Basic' }];
    }
    const token = await getAccessToken();
    return await getRateTypes(
      policy?.itemNo ?? '0',
      stateCode,
      jacket.policyDate,
      false,
      countyCode,
      accountNumber,
      token,
    );
  };

  const {
    execute: executeGetRateTypes,
    status: getRateTypesStatus,
    errors: getRateTypesErrors,
    value: getRateTypesValue,
  } = useAsync<RateTypes[]>(getRateTypesApi, false);

  useEffect(() => {
    if (policy && stateCode && countyCode) {
      executeGetJacketFields();
    }
  }, [policy, stateCode, countyCode]);

  useEffect(() => {
    const rateTypeIdString = rateTypeId.toString();
    if (getJacketFieldsValue) {
      setStatCodesSelections(
        getJacketFieldsValue.policyFields
          .filter((policyField) => policyField.name === 'StatCode')
          .flatMap((statCode) => {
            return statCode.options
              .filter((option) => option.rateTypeValue === rateTypeIdString)
              .map((option) => {
                return {
                  value: option.value,
                  name: option.text,
                };
              });
          }),
      );
    }
  }, [getJacketFieldsValue, rateTypeId]);

  useEffect(() => {
    if (policyDate) {
      executeGetRateTypes();
    }
  }, [policy, policyDate, countyCode]);

  useEffect(() => {
    if (getRateTypesStatus === 'success' && getRateTypesValue) {
      setRateTypesSelections(
        getRateTypesValue.map((rateType) => {
          return {
            value: rateType.rateTypeId,
            name: rateType.rateTypeName,
          };
        }),
      );
    } else if (getRateTypesStatus === 'error') {
      console.error(getRateTypesErrors);
      addGlobalMsg({
        message: 'There was an error while retrieving Rate Types.',
        type: 'error',
      });
      setRateTypesSelections([{ value: '1', name: 'Basic' }]);
    }
  }, [getRateTypesStatus]);

  useEffect(() => {
    // Initialize policy from jacket if policyNumber is set
    if (jacket.policyNumber) {
      const foundPolicy = findPolicy(jacket.policyNumber, stateCode, policyInventory);
      if (foundPolicy) {
        setPolicy(foundPolicy);
      }
    }
  }, [jacket.policyNumber, stateCode, policyInventory]);

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <div className={classes.headerText}>
            {policy ? (
              <>
                <Typography className={classes.title}>{policy.itemDescription}</Typography>
                <Typography className={classes.subtitle}>Policy No: {jacket.policyNumber}</Typography>
              </>
            ) : (
              <Typography className={classes.title}></Typography>
            )}
          </div>
          <IconButton
            className={policy ? classes.deleteButton : classes.deleteButtonNoPolicy}
            onClick={onDelete}
            size="small"
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
        <Divider />
        <div className={classes.policyFields}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <AutocompletePolicySearch
                label="Policy Number"
                name="policyNumberSearchInput"
                hideSearchIcon
                onChange={(policyNumber) => {
                  const input = policyNumber.target?.value;
                  setSearchPolicyNumberInput(input);

                  if (typeof policyNumber === 'string') {
                    const foundPolicy = findPolicy(policyNumber, stateCode, policyInventory);
                    if (foundPolicy !== policy) {
                      setPolicy(foundPolicy);
                      onChange({
                        ...jacket,
                        policyNumber: policyNumber,
                        policyDescription: foundPolicy?.itemDescription ?? '',
                        itemNumber: foundPolicy?.itemNo ?? '',
                      });
                    }
                  } else {
                    setPolicy(null);
                    onChange({
                      ...jacket,
                      policyNumber: '',
                      policyDescription: '',
                      itemNumber: '',
                    });
                  }
                }}
                options={getSearchPolicies(searchPolicyNumberInput, stateCode, policyInventory, selectedPolicies)}
                required
                showValidation
                error={validationErrors?.some((error) => error.field === 'policyNumber')}
                errs={validationErrors}
                helperText={
                  validationErrors?.some((error) => error.field === 'policyNumber') && 'Policy Number is required'
                }
              />
            </Grid>
            <Grid item xs={6} />
            <Grid item xs={12} sm={3}>
              <CurrencyField
                onBlur={(event) => {
                  let liabilityAmount = 0;
                  if (event.target.value) {
                    liabilityAmount = Number(event.target.value.replaceAll(',', ''));
                  }
                  if (liabilityAmount !== jacket.liabilityAmount) {
                    onChange({ ...jacket, liabilityAmount });
                  }
                }}
                fullWidth
                label={'Liability Amount'}
                required
                error={validationErrors?.some((error) => error.field === 'liabilityAmount')}
                errs={validationErrors}
                helperText={
                  validationErrors?.some((error) => error.field === 'liabilityAmount') && 'Liability Amount is required'
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DateFieldString
                fullWidth
                value={policyDate}
                maxDate={getMaxDateString()}
                label={'Policy Date'}
                onChange={(policyDate) => {
                  let formattedPolicyDate = policyDate ? formatDate(policyDate || '') || '' : '';
                  if (new Date(formattedPolicyDate) > new Date()) {
                    formattedPolicyDate = '';
                  }
                  setPolicyDate(formattedPolicyDate);
                  onChange({ ...jacket, policyDate: formattedPolicyDate });
                }}
                required
                showValidation
                error={validationErrors?.some((error) => error.field === 'policyDate')}
                errs={validationErrors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <CurrencyField
                onBlur={(event) => {
                  let reportedGross = 0;
                  if (event.target.value) {
                    reportedGross = Number(event.target.value.replaceAll(',', ''));
                  }
                  if (reportedGross !== jacket.reportedGross) {
                    onChange({ ...jacket, reportedGross });
                  }
                }}
                fullWidth
                label={'Reported Gross'}
                required
                error={validationErrors?.some((error) => error.field === 'reportedGross')}
                errs={validationErrors}
                helperText={
                  validationErrors?.some((error) => error.field === 'reportedGross') && 'Reported Gross is required'
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <CurrencyField
                onBlur={(event) => {
                  let reportedNet = 0;
                  if (event.target.value) {
                    reportedNet = Number(event.target.value.replaceAll(',', ''));
                  }
                  if (reportedNet !== jacket.reportedNet) {
                    onChange({ ...jacket, reportedNet });
                  }
                }}
                fullWidth
                label={'Reported Net'}
                required
                error={validationErrors?.some((error) => error.field === 'reportedNet')}
                errs={validationErrors}
                helperText={
                  validationErrors?.some((error) => error.field === 'reportedNet') && 'Reported Net is required'
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetDropdownSelector
                label={'Rate Type'}
                name={'Rate Type'}
                qaAttribute={'RateType'}
                options={rateTypesSelections}
                disabled={rateTypesSelections.length === 0}
                value={jacket.rateTypeId}
                menuOption={(rateTypeId) => {
                  const rateTypeIdValue = rateTypeId.toString();
                  setRateTypeId(rateTypeIdValue);
                  onChange({ ...jacket, rateTypeId: rateTypeIdValue });
                }}
                required
                error={validationErrors?.some((error) => error.field === 'rateTypeId')}
                errs={validationErrors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              {statCodeRequired && (
                <AgentNetDropdownSelector
                  label={'Stat Code'}
                  name={'Stat Code'}
                  qaAttribute={'StatCode'}
                  options={statCodesSelections}
                  value={jacket.statCode}
                  menuOption={(statCode) => {
                    onChange({ ...jacket, statCode });
                  }}
                  required={statCodeRequired}
                  error={validationErrors?.some((error) => error.field === 'statCode')}
                  errs={validationErrors}
                />
              )}
            </Grid>
          </Grid>
        </div>
      </CardContent>
    </Card>
  );
}
